<template>
  <nav>
    <AlertMessages
      v-if="alertValue"
      :alert-type="alertType" 
      :alert-msg="alertMsg"
      @reset="alertValue = false"
    />
    <v-row
      v-if="!samlConfigurationPage && !loadingData"
      class="fill-height"
      align="center"
    >
      <v-col
        cols="12"
        sm="8"
        md="6"
        offset-sm="2"
        offset-md="3"
        align="center"
      >
        <v-form
          ref="form"
          v-model="valid"
          @keyup.enter="addSAMLConfiguration"
          @submit.prevent
        >
          <v-card
            max-width="800"
            elevation="0"
            rounded="xxl"
            color="background"
          > 
            <v-card-title class="text-h5 font-weight-bold text-center justify-center mb-4 no-break">
              {{ $t('addSAMLConfugurationTitle') }}
            </v-card-title>
            <v-card-text>
              <v-text-field
                v-model="name"
                solo
                hide-details="auto"
                class="rounded-xxl text-body-2"
                :placeholder="$t('addSAMLConfugurationNamePlaceholder')"
                :rules="nameRules"
                data-testid="name-input"
              />
            </v-card-text>
          </v-card>
          <v-row>
            <v-col align="center">
              <FormFooter
                next-button-text="configure"
                :loading="loading"
                cancel-button-link="/space/saml"
                @proceedSubmit="addSAMLConfiguration"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-col>
    </v-row>
    
    <v-row
      v-if="samlConfigurationPage && !loadingData"
      class="fill-height"
      align="center"
    >
      <v-col
        cols="12"
        md="8"
        offset-md="2"
      >
        <p
          align="center"
          class="text-h5 font-weight-bold"
        >
          {{ $t('samlConfigurationTitle') }}
        </p>
      </v-col>
      <v-col
        cols="12"
        md="8"
        offset-md="2"
      >
        <v-form
          ref="form"
          v-model="valid"
          @submit.prevent
        >
          <v-row>
            <v-col
              cols="12"
              md="6"
              class="px-5"
            >
              <v-card
                color="background"
                elevation="0"
              >
                <v-card-title class="text-h6 font-weight-bold mb-4 no-break">
                  {{ $t('idpSettings') }}
                </v-card-title>
                <v-card-text>
                  <v-text-field
                    v-model="idpSSOURL"
                    solo
                    hide-details="auto"
                    class="rounded-xxl text-body-2"
                    :placeholder="$t('SSOURLPlaceholder')"
                    :rules="ssoRules"
                    :error-messages="errorsSSO"
                    data-testid="sso-url-input"
                  />
                  <v-text-field
                    v-model="idpEntityId"
                    solo
                    hide-details="auto"
                    class="rounded-xxl text-body-2"
                    :placeholder="$t('entityIdPlaceholder')"
                    :rules="entityRules"
                    :error-messages="errorsEntityId"
                    data-testid="entity-id-input"
                  />
                </v-card-text>
              </v-card>
              <v-card
                color="background"
                elevation="0"
              >
                <v-card-title class="text-h6 font-weight-bold mb-4 no-break">
                  {{ $t('certificate') }}
                </v-card-title>
                <v-card-text>
                  <v-textarea
                    v-model="certificate"
                    data-testid="certificate-textarea"
                    rounded
                    auto-grow
                    solo
                    name="textarea"
                    :rules="certificateRules"
                    :placeholder="$t('certificatePlaceholder')"
                  />       
                </v-card-text>
              </v-card>
            </v-col>
            <v-col
              cols="12"
              md="6"
              class="px-5"
            >
              <v-card
                color="background"
                elevation="0"
                data-testid="service-provider-card"
              >
                <v-card-title class="text-h6 font-weight-bold mb-4 no-break">
                  {{ $t('serviceProviderDetails') }}
                </v-card-title>
                <v-card-text class="ml-8">
                  <div class="mb-4">
                    <p class="text-h6 darktext--text font-weight-bold mb-0">
                      {{ $t('labelEntityId') }}
                    </p>
                    <span>{{ spEntityId }}</span>
                    <v-icon
                      class="ml-2"
                      data-testid="copy-icon"
                      dense
                      color="darktext"
                      @click="copy(spEntityId)"
                    >
                      mdi-content-copy
                    </v-icon>
                  </div>
                  <div class="mb-4">
                    <p class="text-h6 darktext--text font-weight-bold mb-0">
                      {{ $t('labelACSURL') }}
                    </p>
                    <span>{{ spACSURL }}</span>
                    <v-icon
                      class="ml-2"
                      data-testid="copy-icon"
                      dense
                      color="darktext"
                      @click="copy(spACSURL)"
                    >
                      mdi-content-copy
                    </v-icon>
                  </div>
                  <div class="mb-4">
                    <p class="text-h6 darktext--text font-weight-bold mb-0">
                      {{ $t('labelSingleLogoutService') }}
                    </p>
                    <span>{{ spSingleLogoutService }}</span>
                    <v-icon
                      class="ml-2"
                      data-testid="copy-icon"
                      dense
                      color="darktext"
                      @click="copy(spSingleLogoutService)"
                    >
                      mdi-content-copy
                    </v-icon>
                  </div>
                  <div class="mb-4">
                    <p class="text-h6 darktext--text font-weight-bold mb-0">
                      {{ $t('labelNameIdFormat') }}
                    </p>
                    <span>{{ spNameIdFormat }}</span>
                    <v-icon
                      class="ml-2"
                      data-testid="copy-icon"
                      dense
                      color="darktext"
                      @click="copy(spNameIdFormat)"
                    >
                      mdi-content-copy
                    </v-icon>
                  </div>
                  <v-btn
                    router
                    data-testid="download-button"
                    color="accent"
                    dark
                    elevation="0"
                    class="rounded-xxl text-none px-4"
                    :href="spEntityId" 
                    target="_blank"
                    download
                  >
                    {{ $t('downloadMetadata') }}
                  </v-btn>
                </v-card-text>
              </v-card>
              <v-card
                color="background"
                elevation="0"
                data-testid="mapped-attributes-card"
              >
                <v-card-title class="text-h6 font-weight-bold mb-4 no-break">
                  {{ $t('attributes') }}
                </v-card-title>
                <v-card-text class="ml-8">
                  <div>
                    <p class="text-h6 darktext--text font-weight-bold mb-0">
                      {{ $t('labelMappedAttributes') }}
                    </p>
                    <div
                      v-for="(attribute, index) in mappedAttributes"
                      :key="index"
                    >
                      <span> {{ attribute }} </span>
                      <v-icon
                        class="ml-2"
                        data-testid="copy-icon"
                        dense
                        color="darktext"
                        @click="copy(attribute)"
                      >
                        mdi-content-copy
                      </v-icon>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-row>
            <v-col align="center">
              <FormFooter
                next-button-text="configure"
                :loading="loading"
                cancel-button-link="/space/saml"
                @proceedSubmit="editSAMLConfiguration"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-col>
    </v-row>
  </nav>
</template>
<script>
import i18n from '../../plugins/i18n'
import FormFooter  from '../../components/FormFooter'
import { ruleRequired, ruleValidMin2Max70Characters } from '../../rules/formRules.js';
import AlertMessages  from '../../components/AlertMessages'
import { addSAMLConfiguration, editSAMLConfiguration, getSpaceByURL } from '../../api/spaceApi'

export default {
    components: {
        FormFooter,
        AlertMessages
    },
    data(){
        return{
            valid: true,
            name: '',
            ssoRules: ruleRequired('labelSSOURL'),
            entityRules: ruleRequired('labelEntityId'),
            certificateRules: ruleRequired('labelCertificate'),
            nameRules: ruleRequired('labelName').concat(ruleValidMin2Max70Characters('labelName')),
            alertMsg:'',
            alertValue:false,
            alertType: '',
            spaceId: window.$cookies.get('space_id'),
            loading: false,
            samlConfigurationPage: false,
            idpSSOURL: '',
            idpEntityId: '',
            certificate: '',
            spEntityId: '',
            spACSURL: '',
            spSingleLogoutService: '',
            spNameIdFormat: '',
            mappedAttributes: [],
            id: null,
            errorsSSO: [],
            errorsEntityId: [],
            space: window.location.host.split('.')[0],
            loadingData: false
        }
    },
    watch: {
        idpSSOURL(){
            this.errorsSSO = []
        },
        idpEntityId(){
            this.errorsEntityId = []
        }
    },
    async created(){
      this.loadingData = true
      const responseSpace = await getSpaceByURL(this.space)
      if(responseSpace){
        if(responseSpace.data.data.attributes.idps.length != 0){
          this.$router.push({name: 'SpaceSAML'})
        }
      }
      this.loadingData = false
    },
    methods: {
        async addSAMLConfiguration(){
            this.$refs.form.validate()
            if(this.valid){

                try {
                  let data = {
                        data: {
                            type: "idps", 
                            attributes: {
                                display_name: this.name,
                                space_ids: [this.spaceId]
                            }
                        }
                    };
                    const response = await addSAMLConfiguration(data)
                    if(response.status == 201) {
                      this.spEntityId = response.data.data.meta.entity_id
                      this.spACSURL = response.data.data.meta.assertion_consumer_service
                      this.spSingleLogoutService = response.data.data.meta.single_logout_service
                      this.spNameIdFormat = response.data.data.meta.name_id_format
                      this.mappedAttributes = response.data.data.meta.mapped_attributes
                      this.id = response.data.data.id
                      this.samlConfigurationPage = true
                    }
                } catch (e) {
                    if(e.response){
                        if(e.response.status == 404) {
                            this.$router.push({name: 'PageNotFound'})
                        }
                        else if(e.response.status == 403){
                            this.$router.push({name: 'PageAccessDenied'})
                        } else {
                            this.alertValue = true
                            this.alertType = 'error'
                            this.alertMsg =  i18n.t('serverError')
                        }
                    }            
                }
            }
        },
        async editSAMLConfiguration(){
          this.$refs.form.validate()
          if(this.valid){
              if(!this.isValidHttpUrl(this.idpSSOURL) || !this.isValidHttpUrl(this.idpEntityId)){
                  if(!this.isValidHttpUrl(this.idpSSOURL)) this.errorsSSO = [i18n.t('SSOURLErrorValid')] 
                  if(!this.isValidHttpUrl(this.idpEntityId)) this.errorsEntityId = [i18n.t('EntityIdURLErrorValid')] 
              }else {
                  try {
                      let data = {
                            data: {
                                type: "idps",
                                id: this.id, 
                                attributes: {
                                    display_name: this.name,
                                    enabled: true,
                                    regular_login_disabled: false,
                                    direct_redirect: false,
                                    idp_entityid: this.idpEntityId,
                                    idp_sso_url: this.idpSSOURL,
                                    idp_x509: this.certificate,
                                    space_ids: [this.spaceId]
                                }
                            }
                        };
                        const response = await editSAMLConfiguration(this.id, data)
                        if(response.status == 200) {
                          this.$router.push({name: 'SpaceSAML', params: {'successfullyAddedSAMLConfiguration': true}})
                        }
                    } catch (e) {
                        if(e.response){
                            if(e.response.status == 404) {
                                this.$router.push({name: 'PageNotFound'})
                            }
                            else if(e.response.status == 403){
                                this.$router.push({name: 'PageAccessDenied'})
                            } else {
                                this.alertValue = true
                                this.alertType = 'error'
                                this.alertMsg =  i18n.t('serverError')
                            }
                        }            
                    }
                }
            }
        },
        copy(text){
            const el = document.createElement('textarea');  
            el.value = text
            document.body.appendChild(el);                          
            el.select();                                    
            document.execCommand('copy');                   
            document.body.removeChild(el);
        },
        isValidHttpUrl(url) {
            let urlCheck;
            try {
                urlCheck = new URL(url);
            } catch (_) {
                return false;  
            }
            return urlCheck.protocol === "http:" || urlCheck.protocol === "https:";
        }
    },
}
</script>
